import React, { useRef, useEffect } from 'react';
import Chart from 'chart.js/auto';
import { Row, Col, Space, DatePicker } from 'antd';
import styled from 'styled-components';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import InfoPopover from 'modules/Common/Components/InfoPopover';
import { useGraphDataScaler } from './Hooks/useGraphDataScaler';
import { externalTooltipHandler } from './ExternalTooltipHandler';
import LegendLabel from './LegendLabel';
import SelectWithIcon from 'modules/Campaigns/Shared/SelectWithIcon';
import { ReactComponent as CalenderIcon } from '../../../assets/img/icons/Campaign/Calender.svg';
import { ReactComponent as AnalyticsSmallIcon } from '../../../assets/img/icons/AnalyticsSmallIcon.svg';
import { setWorkflowGraphDate } from '../actions';
import GlobalAntButton from 'modules/Common/Components/GlobalAntButton';

interface ICustomChartProps {
  title?: any;
  boxRightOffset?: any;
  boxBottomOffset?: any;
  annotationHeight?: any;
  campaignId?: any;
  emailId?: any;
  tooltip?: string;
  emptyMessage?: string;
}
const gradientColor = [['rgba(255, 150, 45, 0.5)', 'rgba(255, 150, 45, 0.1)']];
let gradients: any = [];

const CustomChart: React.FC<ICustomChartProps> = React.memo((props: ICustomChartProps) => {
  const canvasRef: any = useRef('');
  const [graphData, isTodayDateInData, setColorGradients]: any = useGraphDataScaler(gradients);
  const datesOptions = useSelector((state: RootState) => state.analyticsGraph.dates);
  const selectedDatesObject = useSelector((state: RootState) => state.automation.workflowEmailGraph.selectedDateFilter);

  useEffect(() => {
    if (canvasRef && canvasRef.current && graphData && graphData?.pDatasets?.length > 0 && graphData?.xAxesDateLabels?.length > 0) {
      const someChart = new Chart(canvasRef.current, {
        type: 'line',
        data: {
          labels: graphData.xAxesDateLabels,
          datasets: graphData.pDatasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          elements: {
            line: {
              tension: 0.5,
            },
          },
          plugins: {
            tooltip: {
              enabled: false,
              position: 'nearest',
              external: externalTooltipHandler,
            },
            legend: {
              display: true,
              position: 'top',
              align: 'start',
              textDirection: 'ltr',
              title: {
                display: true,
                text: ' ',
                position: 'start',
                font: {
                  size: 18,
                },
                padding: {
                  top: 10,
                  bottom: 10,
                },
              },
              labels: {
                usePointStyle: true,
                textAlign: 'center',
                generateLabels: function (chart) {
                  return [];
                },
              },
            },
          },
          scales: {
            xAxes: {
              ticks: {
                callback: function (label) {
                  return `${graphData.xAxesDateLabels[label].split('/').slice(0, 2).join('/')}`;
                },
              },
              grid: {
                display: false,
                borderWidth: 0,
              },
            },
            yAxes: {
              display: false,
              beginAtZero: true,
            },
          },
        },
      });
      return () => {
        someChart.destroy();
      };
    }
  }, [graphData]);

  useEffect(() => {
    gradients = gradientColor.map((colorArr) => {
      let gradient: any;
      gradient = canvasRef.current.getContext('2d').createLinearGradient(10, 400, 0, 0);
      gradient.addColorStop(0, colorArr[1]);
      gradient.addColorStop(1, colorArr[0]);
      return gradient;
    });
    setColorGradients(gradients);
  }, [canvasRef.current]);

  return (
    <Wrapper>
      <div className="custom-legend d-flex justify-content-between">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="chart-title">{props?.title}</span>
          <span style={{ display: 'flex', marginLeft: '12px', cursor: 'pointer' }}>
            {props?.tooltip ? <InfoPopover placement="bottomLeft" infoText={props?.tooltip} width={341} /> : <></>}
          </span>
        </div>
        <div>
          <div className="filter-col d-flex" style={{ gap: '8px' }}>
            {/* <GlobalAntButton
              btnText={'View Email Engagement'}
              icon={<AnalyticsSmallIcon />}
              buttonType={'type'}
              btnType={'default'}
              disabled={true}
              loading={false}
              onClick={() => {}}
              border={' 1px solid #1EC252'}
              padding={'0px 10px'}
              bgColor={`${false ? 'rgb(126 126 126 / 42%)' : '#fff'}`}
              color={'#1EC252'}
              customIcon={''}
              borderRadius={'8px'}
              fontSize={'14px'}
              boxShadow={''}
              maxWidth={''}
              width={'auto'}
            /> */}
            <SelectWithIcon
              emoji={<CalenderIcon style={{ height: 18, width: 18 }} />}
              options={datesOptions}
              heightWidth={15}
              id="date"
              value={selectedDatesObject}
              {...(setWorkflowGraphDate
                ? {
                    onChange: (value) => {
                      setWorkflowGraphDate(value);
                    },
                  }
                : {})}
              customDatePicker={<DatePicker bordered={false} />}
              isWorkflowGraph={true}
            />
          </div>
        </div>
      </div>
      <div className="legend-parent-div">
        {graphData?.legendLabels && graphData?.legendLabels[0] && (
          <div className="legend-label-div">
            {graphData?.legendLabels?.map((label: any, index: any) => {
              return (
                <LegendLabel key={`legend-label-${index}-${label}`} label={label.name} index={index} svgComponent={label?.icon ? label.icon : null} />
              );
            })}
          </div>
        )}
      </div>
      <CanvasContainer>
        {graphData?.xAxesDateLabels?.length === 0 && (
          <Col span={24}>
            <div className="d-flex justify-content-center align-items-center empty-graph">
              <h2>{props?.emptyMessage ? props.emptyMessage : 'Your open and click stats will be shown here'}</h2>
            </div>
          </Col>
        )}
        <canvas ref={canvasRef} id="myChart" height={480}></canvas>
        {graphData?.xAxesDateLabels?.length > 0 && isTodayDateInData && (
          <BoxAnnotation
            annotationHeight={props.annotationHeight ? props.annotationHeight : '75%'}
            bottomOffset={props?.boxBottomOffset ? props?.boxBottomOffset : '8%'}
            rightOffset={props?.boxRightOffset ? props?.boxRightOffset : '3%'}
          ></BoxAnnotation>
        )}
      </CanvasContainer>
    </Wrapper>
  );
});

export default CustomChart;
interface ICanvasContainerProps {
  backgroundImage?: any;
  backgroundSize?: any;
}
const Wrapper = styled.div`
  padding-inline: 24px;
  padding-top: 12px;
  padding-bottom: 16px;
  div[class$='-control'] {
    width: 134px;
  }
  .css-1uxsi1k-menu {
    right: 0;
  }
  #chartjs-tooltip {
    z-index: 2 !important;
  }
  .global-btn-style {
    display: flex;
    @media all and (max-width: 1439px) and (max-height: 729px) and (-webkit-device-pixel-ratio: 1.25) {
      height: 30px !important;
      min-height: 30px !important;
    }
  }
  .custom-legend {
    width: 100%;
    display: flex;
    align-items: center;
  }
  .chart-title {
    color: #424b50;
    font-family: 'Roboto Slab';
    font-size: 28px;
    font-style: normal;
    font-weight: 700;
    line-height: 47px; /* 167.857% */
    letter-spacing: -0.7px;
    @media all and (max-width: 1439px) {
      font-size: 20px;
    }
  }
  .legend-parent-div {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    margin-top: 16px;
  }
  .legend-label-div {
    display: flex;
    align-items: center;
    gap: 15px;
  }
  .legend-label-text {
    margin-bottom: 0;
  }
  .legend-label-text {
    color: #7e7e7e;
    font-family: 'Roboto';
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: 0.429px;
  }
  .legend-label {
    gap: 4px;
  }
`;
const CanvasContainer = styled('div')<ICanvasContainerProps>`
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  .empty-graph {
    min-height: 400px;
    max-height: 400px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      min-height: 250px;
      max-height: 250px;
    }
    h2 {
      color: #cacaca;
      font-size: 23px;
      font-weight: 400;
      font-family: 'Roboto Slab', serif;
      line-height: 32px;
      letter-spacing: -0.5px;
      margin-bottom: 100px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 16px;
        line-height: 23px;
        margin-bottom: 40px;
      }
    }
  }
  canvas {
    /* background-color: white; */
    width: 100%;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 480px;
    min-height: 480px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      max-height: 300px;
      min-height: 300px;
    }
  }
  .annotation {
    position: absolute;
    background-color: #cae0f3;
    border-radius: 6.5px;
    width: 13px;
    height: 75%;
    opacity: 0.74;
    right: 3%;
    bottom: 8%;
  }
  .no-stats {
    font-weight: bold;
    font-size: x-large;
    margin-top: 170px;
    margin-left: -30px;
    color: darkgray;
  }
`;
interface IBoxAnnotationProps {
  bottomOffset?: string;
  rightOffset?: string;
  annotationHeight?: string;
}
const BoxAnnotation = styled('div')<IBoxAnnotationProps>`
  position: absolute;
  background-color: #cae0f3;
  border-radius: 6.5px;
  width: 13px;
  height: ${(props) => props.annotationHeight};
  opacity: 0.74;
  right: ${(props) => props.rightOffset};
  bottom: ${(props) => props.bottomOffset};
`;
